import { useEffect, useState } from "react";
import { Button, Group, Modal, Stack, Switch, Table, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { DeliveryType } from "../../api/types";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

export function DeliveryTypesModal({
  opened,
  onClose,
  onChanged,
}: {
  opened: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [types, setTypes] = useState<DeliveryType[]>([]);
  const [name, setName] = useState("");
  const [marksDebt, setMarksDebt] = useState(false);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      setTypes(await api.listDeliveryTypes());
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    }
  }

  useEffect(() => {
    if (opened) void load();
  }, [opened]);

  async function add() {
    const v = name.trim();
    if (!v) return;
    setSaving(true);
    try {
      await api.createDeliveryType(v, marksDebt);
      setName("");
      setMarksDebt(false);
      await load();
      onChanged();
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    } finally {
      setSaving(false);
    }
  }

  async function patch(tdt: DeliveryType, p: { marks_debt?: boolean; active?: boolean }) {
    try {
      await api.updateDeliveryType(tdt.id, p);
      await load();
      onChanged();
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    }
  }

  async function remove(tdt: DeliveryType) {
    if (!window.confirm(t("shipments.deliveryTypes.confirmDelete", { name: tdt.name }))) return;
    try {
      await api.deleteDeliveryType(tdt.id);
      await load();
      onChanged();
    } catch (err) {
      const code = err instanceof ApiError ? (err.detail as { error?: string } | null)?.error : undefined;
      const msg =
        code === "builtin_protected"
          ? t("shipments.deliveryTypes.builtinProtected")
          : code === "type_in_use"
            ? t("shipments.deliveryTypes.inUse")
            : err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({ color: "red", message: msg });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("shipments.deliveryTypes.title")} centered>
      <Stack>
        <Group align="end" gap="xs">
          <TextInput
            label={t("shipments.deliveryTypes.name")}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void add(); }}
            style={{ flex: 1 }}
          />
          <Switch
            label={t("shipments.deliveryTypes.marksDebt")}
            checked={marksDebt}
            onChange={(e) => setMarksDebt(e.currentTarget.checked)}
          />
          <Button leftSection={<IconPlus size={14} />} loading={saving} onClick={() => void add()}>
            {t("common.add")}
          </Button>
        </Group>
        {types.length === 0 ? (
          <Text c="dimmed" size="sm">{t("shipments.deliveryTypes.empty")}</Text>
        ) : (
          <ResponsiveTable
            mobile={{
              rows: types,
              rowKey: (tdt) => tdt.id,
              title: (tdt) => tdt.name,
              alwaysOpen: true,
              fields: [
                {
                  label: t("shipments.deliveryTypes.marksDebt"),
                  render: (tdt) => (
                    <Switch checked={tdt.marks_debt} onChange={() => void patch(tdt, { marks_debt: !tdt.marks_debt })} size="xs" />
                  ),
                },
                {
                  label: t("common.active"),
                  render: (tdt) => (
                    <Switch
                      checked={tdt.active}
                      onChange={() => void patch(tdt, { active: !tdt.active })}
                      label={tdt.active ? t("common.active") : t("common.inactive")}
                      size="xs"
                    />
                  ),
                },
              ],
              actions: (tdt) =>
                !tdt.slug ? (
                  <Button size="compact-md" variant="subtle" color="red" onClick={() => void remove(tdt)}>
                    <IconTrash size={16} />
                  </Button>
                ) : null,
            }}
          >
          <Table>
            <Table.Tbody>
              {types.map((tdt) => (
                <Table.Tr key={tdt.id}>
                  <Table.Td>{tdt.name}</Table.Td>
                  <Table.Td w={130}>
                    <Switch
                      checked={tdt.marks_debt}
                      onChange={() => void patch(tdt, { marks_debt: !tdt.marks_debt })}
                      label={t("shipments.deliveryTypes.marksDebt")}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td w={110}>
                    <Switch
                      checked={tdt.active}
                      onChange={() => void patch(tdt, { active: !tdt.active })}
                      label={tdt.active ? t("common.active") : t("common.inactive")}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td w={40}>
                    {!tdt.slug && (
                      <Button size="compact-xs" variant="subtle" color="red" onClick={() => void remove(tdt)}>
                        <IconTrash size={14} />
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </ResponsiveTable>
        )}
      </Stack>
    </Modal>
  );
}
